import React, { useState, useRef } from 'react';
import { uploadImage } from '../utils/imageUpload';
import { UserCircleIcon } from '@heroicons/react/24/outline';
import imageCompression from 'browser-image-compression';

const ImageUpload = ({ 
  currentImage, 
  onUploadSuccess, 
  path,  // Single path prop that parent components will provide
  className = '' 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setError('');
    
    try {
      // Compress the image before upload
      const options = {
        maxSizeMB: 0.15, // Target size (e.g., 0.15MB = 150kB)
        maxWidthOrHeight: 800, // Max width or height in px
        useWebWorker: true,
      };
      const compressedFile = await imageCompression(file, options);

      await uploadImage({
        file: compressedFile,
        path,
        onProgress: (progress) => setUploadProgress(progress),
        onError: (err) => setError(err),
        onSuccess: (url) => {
          onUploadSuccess(url);
          setUploadProgress(100);
        }
      });
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative w-full h-full rounded-full overflow-hidden bg-gray-100 group">
        {currentImage ? (
          <img 
            src={currentImage}
            alt="Profile" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '';
              e.target.className = 'hidden';
            }}
          />
        ) : (
          <UserCircleIcon className="w-full h-full text-gray-400" />
        )}

        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded-full p-2 shadow-lg hover:bg-gray-50"
            title="Upload new photo"
          >
            {isUploading ? (
              <div className="w-6 h-6 border-2 border-t-blue-500 rounded-full animate-spin" />
            ) : (
              <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        className="hidden"
      />

      {error && (
        <div className="absolute -bottom-8 left-0 right-0 text-red-500 text-xs text-center">
          {error}
        </div>
      )}

      {isUploading && (
        <div className="absolute -bottom-6 left-0 right-0 h-1 bg-gray-200 rounded">
          <div 
            className="h-full bg-blue-500 rounded transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageUpload;